import { useState } from "react";
import { X, Sparkle, Translate } from "@phosphor-icons/react";

const LANGS_BRIEF = [
  { v: "en", name: "English" },
  { v: "hi", name: "हिन्दी (Hindi · Devanagari)" },
  { v: "bn", name: "বাংলা (Bengali)" },
];
const LANGS_CD = [...LANGS_BRIEF, { v: "ar", name: "العربية (Arabic · RTL)" }];
const TONES = [
  { v: "firm", name: "Firm (standard)" },
  { v: "escalation", name: "Escalation (warning)" },
  { v: "final_notice", name: "Final notice (pre-litigation)" },
];

/**
 * @param mode "brief" | "cd"
 * @param onSubmit({language, force} | {language, tone, recipient_name, recipient_address})
 */
export default function AILanguageToneModal({ mode = "brief", onClose, onSubmit, submitting }) {
  const isCD = mode === "cd";
  const isBrief = mode === "brief";
  // Pre-fill from localStorage (CEO Q · per-user cache)
  const lsKey = isCD ? "span_cd_prefs" : "span_brief_prefs";
  const saved = JSON.parse(localStorage.getItem(lsKey) || "{}");
  const [language, setLanguage] = useState(saved.language || "en");
  const [tone, setTone] = useState(saved.tone || "firm");
  const [recipientName, setRecipientName] = useState("");
  const [recipientAddress, setRecipientAddress] = useState("");
  const [force, setForce] = useState(false);

  const submit = () => {
    const payload = isCD
      ? { language, tone, recipient_name: recipientName, recipient_address: recipientAddress, force: true }
      : { language, force };
    localStorage.setItem(lsKey, JSON.stringify({ language, tone }));
    onSubmit(payload);
  };

  const langOptions = isCD ? LANGS_CD : LANGS_BRIEF;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="bg-white max-w-md w-full p-5 space-y-3" data-testid={`ai-${mode}-modal`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="label-eyebrow flex items-center gap-1">
              {isCD ? <Translate size={12}/> : <Sparkle size={12}/>}
              {isCD ? "Cease & Desist letter" : "AI Field Officer Brief"}
            </div>
            <div className="font-display font-bold text-lg mt-0.5">Choose language &amp; tone</div>
          </div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="text-[11px] text-[var(--span-text-2)]">Powered by Claude Sonnet 4.5 · cached per language for this case.</div>

        <div>
          <label className="label-eyebrow mb-1 block">Language</label>
          <div className="grid grid-cols-1 gap-1">
            {langOptions.map(l => (
              <label key={l.v} className={`hairline px-3 py-1.5 cursor-pointer text-[12px] ${language===l.v ? "bg-emerald-50 border-emerald-400" : ""}`} data-testid={`lang-${l.v}`}>
                <input type="radio" name="lang" value={l.v} checked={language===l.v} onChange={e=>setLanguage(e.target.value)} className="mr-2"/>
                {l.name}
              </label>
            ))}
          </div>
        </div>

        {isCD && (
          <>
            <div>
              <label className="label-eyebrow mb-1 block">Tone</label>
              <div className="grid grid-cols-1 gap-1">
                {TONES.map(t => (
                  <label key={t.v} className={`hairline px-3 py-1.5 cursor-pointer text-[12px] ${tone===t.v ? "bg-emerald-50 border-emerald-400" : ""}`} data-testid={`tone-${t.v}`}>
                    <input type="radio" name="tone" value={t.v} checked={tone===t.v} onChange={e=>setTone(e.target.value)} className="mr-2"/>
                    {t.name}
                  </label>
                ))}
              </div>
            </div>
            <input data-testid="cd-recipient-name" placeholder="Recipient name (optional)" value={recipientName} onChange={e=>setRecipientName(e.target.value)} className="w-full hairline px-2 py-1.5 text-[12px]"/>
            <input data-testid="cd-recipient-addr" placeholder="Recipient address (optional)" value={recipientAddress} onChange={e=>setRecipientAddress(e.target.value)} className="w-full hairline px-2 py-1.5 text-[12px]"/>
          </>
        )}

        {isBrief && (
          <label className="flex items-center gap-2 text-[11px] text-[var(--span-text-2)] cursor-pointer" data-testid="brief-force">
            <input type="checkbox" checked={force} onChange={e=>setForce(e.target.checked)}/>
            Force regenerate (ignore cached brief)
          </label>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="text-[11px] hairline px-3 py-1.5">Cancel</button>
          <button data-testid={`generate-${mode}`} onClick={submit} disabled={submitting} className="span-btn">
            {submitting ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>
    </div>
  );
}
